import { METHODNAMES } from './constants';
import { v4 as uuidv4 } from 'uuid';
import { FunctionObject } from './helper';
 
const pendingRequests = new Map();
 
const disconnectListeners = new Map(); // Store persistent disconnect handlers
 
const handleMessageResponse = (event) => {
  if (!event.detail?.eventName) return;
 
  const { eventName, detail } = event.detail;
 
  // Handle pending request response
  if (pendingRequests.has(eventName)) {
    const requestHandler = pendingRequests.get(eventName);
    if (requestHandler) {
      clearTimeout(requestHandler.timeoutId);
      requestHandler.resolve(detail);
      pendingRequests.delete(eventName);
    }
  }
 
  // Handle disconnect event separately
  if (eventName.startsWith(`kalp:${METHODNAMES.DISCONNECT_WALLET}_`)) {
    const dappURL = eventName.split('_').pop(); // Extract DApp URL
    if (dappURL && disconnectListeners.has(dappURL)) {
      disconnectListeners.get(dappURL)?.(); // Trigger disconnect callback
    }
  }
};
 
// Listen for all `kalpResponse` events
if (typeof window !== 'undefined') {
  document.addEventListener('kalpResponse', (event) => {
    handleMessageResponse(event);
  });
}
 
const dispatchMessage = (
  methodName,
  dappToken,
  dappName,
  dappURL,
  dappIcon,
  methodArgs = [],
  handleDisconnect
) => {
  return new Promise((resolve, reject) => {
    const requestId = uuidv4();
    const eventName = `kalp:${methodName}_${dappURL}`;
 
    // Timeout mechanism
    const timeoutId = setTimeout(() => {
      console.error(`Timeout: No response received for ${eventName}`);
      reject(new Error(`Timeout: No response received for ${eventName}`));
      pendingRequests.delete(eventName);
    }, 50000);
 
    // Store request in pendingRequests
    pendingRequests.set(eventName, { resolve, reject, timeoutId });
 
    // Store persistent disconnect handler if provided
    if (handleDisconnect) {
      disconnectListeners.set(dappURL, handleDisconnect);
    }
 
    // Message payload
    const messagePayload = {
      dappToken,
      dappName,
      dappIcon: dappIcon || '',
      dappURL,
      methodArgs,
      methodCallId: requestId,
      methodName,
    };
 
    const event = new CustomEvent('kalpRequest', {
      detail: { eventName, detail: messagePayload },
    });
    window.dispatchEvent(event);
  });
};
 
// DApp communication functions
export const registerDAppToken = (
  dappToken,
  dappName,
  dappURL,
  dappIcon,
  network,
  handleDisconnect
) =>
  dispatchMessage(
    METHODNAMES.CONNECT_TO_WALLET,
    dappToken,
    dappName,
    dappURL,
    dappIcon,
    [network],
    handleDisconnect
  );
 
export const reEngagingEvents = (
  dappToken,
  dappName,
  dappURL,
  dappIcon
) =>
  dispatchMessage(
    METHODNAMES.REENGAGE_EVENTS,
    dappToken,
    dappName,
    dappURL,
    dappIcon
  );
 
export const getEnrollmentId = (
  dappToken,
  dappName,
  dappURL,
  dappIcon
) =>
  dispatchMessage(
    METHODNAMES.GET_ENROLLMENTID,
    dappToken,
    dappName,
    dappURL,
    dappIcon
  );
 
export const writeTransaction = (
  dappToken,
  dappName,
  dappURL,
  dappIcon,
  channelName,
  chaincodeName,
  transactionName,
  transactionParams
) =>
  dispatchMessage(
    METHODNAMES.WRITE_TRANSACTION,
    dappToken,
    dappName,
    dappURL,
    dappIcon,
    [channelName, chaincodeName, transactionName, transactionParams]
  );
 
export const readTransaction = (
  dappToken,
  dappName,
  dappURL,
  dappIcon,
  channelName,
  chaincodeName,
  transactionName,
  transactionParams
) =>
  dispatchMessage(
    METHODNAMES.READ_TRANSACTION,
    dappToken,
    dappName,
    dappURL,
    dappIcon,
    [channelName, chaincodeName, transactionName, transactionParams]
  );
 
export const disconnect = (
  dappToken,
  dappName,
  dappURL
) =>
  dispatchMessage(METHODNAMES.DISCONNECT_WALLET, dappToken, dappName, dappURL);
 
export const getCurrentNetwork = (
  dappToken,
  dappName,
  dappURL,
  dappIcon
) =>
  dispatchMessage(
    METHODNAMES.GET_CURRENT_NETWORK,
    dappToken,
    dappName,
    dappURL,
    dappIcon
  );